import { Trans, useTranslation } from 'react-i18next';

import Link from '@/components/atoms/Link/Link';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { LiveChatConnectError } from '@/shared/types/integration';
import { LIVECHAT_APPS_GUIDE_URL } from '@/util/constants';

const AUTHORIZATION_DOCS_URL =
  'https://platform.text.com/docs/guides/console-guides/app-authorization';

// `access_denied` comes from LiveChat when the admin denies the consent.
type ErrorCode = LiveChatConnectError | 'access_denied';

const ERRORS: Record<ErrorCode, { i18nKey: string; href?: string }> = {
  not_installed: {
    i18nKey: 'integrations.livechat.errors.not_installed',
    href: `${LIVECHAT_APPS_GUIDE_URL}#step-4-testing-your-application`,
  },
  invalid_credentials: {
    i18nKey: 'integrations.livechat.errors.invalid_credentials',
    href: AUTHORIZATION_DOCS_URL,
  },
  not_authorized: {
    i18nKey: 'integrations.livechat.errors.not_authorized',
    href: 'https://platform.text.com/docs/authorization/scopes',
  },
  already_connected: { i18nKey: 'integrations.livechat.errors.already_connected' },
  access_denied: {
    i18nKey: 'integrations.livechat.errors.access_denied',
    href: AUTHORIZATION_DOCS_URL,
  },
  oauth_failed: {
    i18nKey: 'integrations.livechat.errors.oauth_failed',
    href: LIVECHAT_APPS_GUIDE_URL,
  },
};

const ConnectError = ({ code }: { code: string }) => {
  const { t } = useTranslation();
  const { i18nKey, href } = Object.hasOwn(ERRORS, code)
    ? ERRORS[code as ErrorCode]
    : ERRORS.oauth_failed;

  return (
    <Alert variant="destructive">
      <AlertTitle>{t('integrations.livechat.errors.title')}</AlertTitle>
      <AlertDescription>
        <p>
          <Trans
            i18nKey={i18nKey}
            components={href ? { link1: <Link href={href} external /> } : undefined}
          />
        </p>
      </AlertDescription>
    </Alert>
  );
};

export default ConnectError;
